/**
 * `/eingang` — was hereinkam.
 *
 * Die Liste beantwortet eine Frage: Womit muss sich ein Mensch beschaeftigen?
 * Deshalb steht die Warnung neben dem Betrag und nicht in einer Spalte ganz
 * rechts, und deshalb sind „nur mit Warnungen" und „Prüfung" zwei Klicks weit.
 *
 * Sortiert ist nach Eingang, neueste zuerst — wer den Eingang oeffnet, will
 * wissen, was gerade kam.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertOctagon, Search } from 'lucide-react';

import { bh, fehlerText, type BelegFilter } from '@/lib/api';
import { ART_LABEL, STATUS_LABEL } from '@/lib/beleg';
import { datum, euro } from '@/lib/format';
import { AmpelChip, Chip } from '@/components/ui/Chip';
import { Auswahl, Eingabe, Feld, Seitenkopf } from '@/components/ui/Feld';
import { Fehler, Laden, Leer } from '@/components/ui/Zustaende';
import type { Belegart, Belegstatus } from '@/types';

const STATUS_REIHE: Belegstatus[] = [
  'neu',
  'pruefung',
  'geprueft',
  'freigegeben',
  'uebergeben',
  'abgelehnt',
  'duplikat',
];

const ART_REIHE: Belegart[] = [
  'rechnung',
  'gutschrift',
  'mahnung',
  'bwa',
  'opos',
  'kontoauszug',
  'sonstiges',
  'unklar',
];

export function EingangPage() {
  const [status, setStatus] = useState<Belegstatus | ''>('');
  const [art, setArt] = useState<Belegart | ''>('');
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [suche, setSuche] = useState('');
  const [nurMitWarnungen, setNurMitWarnungen] = useState(false);

  const filter: BelegFilter = useMemo(
    () => ({
      status: status ? [status] : undefined,
      art: art ? [art] : undefined,
      nur_mit_warnungen: nurMitWarnungen,
      von: von || undefined,
      bis: bis || undefined,
      suche: suche || undefined,
    }),
    [status, art, nurMitWarnungen, von, bis, suche],
  );

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['belege', filter],
    queryFn: () => bh.belege(filter),
  });

  const gefiltert = Boolean(status || art || von || bis || suche || nurMitWarnungen);

  return (
    <div>
      <Seitenkopf
        titel="Eingang"
        beschreibung="Was hereinkam: aus dem Sammelpostfach, aus dem Scan und aus den persönlichen Postfächern."
      >
        {data && (
          <span className="text-sm text-slate-500">
            {data.total} {data.total === 1 ? 'Beleg' : 'Belege'}
          </span>
        )}
      </Seitenkopf>

      <div className="mb-4 grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-5">
        <Feld label="Status">
          <Auswahl value={status} onChange={(e) => setStatus(e.target.value as Belegstatus | '')}>
            <option value="">alle</option>
            {STATUS_REIHE.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </Auswahl>
        </Feld>

        <Feld label="Art">
          <Auswahl value={art} onChange={(e) => setArt(e.target.value as Belegart | '')}>
            <option value="">alle</option>
            {ART_REIHE.map((a) => (
              <option key={a} value={a}>
                {ART_LABEL[a]}
              </option>
            ))}
          </Auswahl>
        </Feld>

        <Feld label="Eingang ab">
          <Eingabe type="date" value={von} onChange={(e) => setVon(e.target.value)} />
        </Feld>

        <Feld label="Eingang bis">
          <Eingabe type="date" value={bis} onChange={(e) => setBis(e.target.value)} />
        </Feld>

        <Feld label="Suche" hinweis="Aussteller, Nummer, Dateiname">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Eingabe
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              placeholder="Telekom, RE-2026-…"
              className="pl-8"
            />
          </div>
        </Feld>

        <label className="flex items-center gap-2 text-sm text-slate-600 lg:col-span-5">
          <input
            type="checkbox"
            checked={nurMitWarnungen}
            onChange={(e) => setNurMitWarnungen(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
          />
          Nur Belege mit Warnungen
        </label>
      </div>

      {isLoading && <Laden />}
      {isError && <Fehler text={fehlerText(error)} onErneut={() => void refetch()} />}

      {data && data.items.length === 0 && (
        <Leer
          titel={gefiltert ? 'Kein Beleg passt zu diesem Filter.' : 'Noch kein Beleg da.'}
          hinweis={
            gefiltert
              ? 'Filter zurücksetzen und noch einmal schauen.'
              : 'Belege kommen aus dem Sammelpostfach, über den Kurzbefehl oder über die Seite „Scan“.'
          }
        />
      )}

      {data && data.items.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Art</th>
                <th className="px-4 py-3">Aussteller</th>
                <th className="px-4 py-3 text-right">Betrag</th>
                <th className="px-4 py-3">Fällig</th>
                <th className="px-4 py-3">Warnungen</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((zeile) => (
                <tr
                  key={zeile.id}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/eingang/${zeile.id}`}
                      className="inline-flex flex-col gap-1 font-medium text-slate-900 hover:text-primary-700"
                    >
                      {STATUS_LABEL[zeile.status]}
                      <span className="text-xs font-normal text-slate-400">
                        {datum(zeile.created_at)}
                      </span>
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Chip>{ART_LABEL[zeile.art]}</Chip>
                    {/* Gemahnt ist keine Warnung, sondern ein Zustand — und
                        einer, der ganz nach oben gehoert (Ablauf 4a). */}
                    {zeile.gemahnt && (
                      <Chip klassen="ml-1 border-danger-200 bg-danger-50 text-danger-700">
                        <AlertOctagon size={12} />
                        gemahnt
                      </Chip>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/eingang/${zeile.id}`} className="block hover:text-primary-700">
                      <span className="font-medium text-slate-900">
                        {zeile.supplier_name ?? <span className="text-slate-400">unbekannt</span>}
                      </span>
                      <span className="block text-xs text-slate-400">
                        {zeile.invoice_number ?? zeile.original_name}
                      </span>
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-slate-900">
                    {euro(zeile.amount_gross)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                    {datum(zeile.due_date)}
                  </td>
                  <td className="px-4 py-3">
                    <AmpelChip rot={zeile.warnungen_rot} gelb={zeile.warnungen_gelb} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
